import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Send, Mail, Loader2, ShieldCheck,
  Sparkles, FileText, Wallet, CheckCircle2, LogOut, Filter,
  Zap, Bell, BellOff, AlertTriangle, Copy, ChevronDown, Check,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "gts_admin_token";

const KIND_META = {
  quiz: { label: "Quiz", icon: Sparkles, colour: "#B8472D" },
  eu_quiz: { label: "EU Checklist", icon: Sparkles, colour: "#3D5A80" },
  checklist: { label: "Checklist", icon: FileText, colour: "#6B7A4F" },
  calculator: { label: "Calculator", icon: Wallet, colour: "#C9A24A" },
};

const TIER_META = {
  ready: { label: "Ready", bg: "#B8472D", fg: "#FAF6EE" },
  nearly: { label: "Nearly", bg: "#C9A24A", fg: "#1A1A1A" },
  building: { label: "Building", bg: "#6B7A4F", fg: "#FAF6EE" },
  exploring: { label: "Exploring", bg: "#6B6258", fg: "#FAF6EE" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "booked", label: "Booked" },
  { value: "opted_out", label: "Opted out" },
  { value: "closed", label: "Closed" },
];

const STATUS_META = {
  new: { bg: "#1B2D4F", fg: "#FAF6EE" },
  contacted: { bg: "#C9A24A", fg: "#1A1A1A" },
  booked: { bg: "#6B7A4F", fg: "#FAF6EE" },
  opted_out: { bg: "#6B6258", fg: "#FAF6EE" },
  closed: { bg: "#8A8A8A", fg: "#FAF6EE" },
};

async function api(path, token, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", "X-Admin-Token": token, ...(opts.headers || {}) },
  });
  if (res.status === 401) throw new Error("Unauthorised");
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && body.detail) detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
    } catch (_) { /* body wasn't JSON */ }
    throw new Error(detail);
  }
  return res.json();
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [inputToken, setInputToken] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_kind: {} });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState("");
  const [autoInfo, setAutoInfo] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  const refresh = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const q = filter === "all" ? "" : `?kind=${filter}`;
      const [statsRes, leadsRes] = await Promise.all([
        api("/api/admin/leads/stats", t),
        api(`/api/admin/leads${q}`, t),
      ]);
      setStats(statsRes);
      setLeads(leadsRes.leads);
      setAuthed(true);
      api("/api/admin/audiences", t).then(setAutoInfo).catch(() => setAutoInfo(null));
    } catch (e) {
      setError(e.message);
      if (e.message === "Unauthorised") {
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (token) refresh(token);
  }, [token, filter, refresh]);

  const login = (e) => {
    e.preventDefault();
    localStorage.setItem(TOKEN_KEY, inputToken.trim());
    setToken(inputToken.trim());
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  const sendFollowup = async (leadId) => {
    if (!window.confirm("Send the follow-up email to this lead now?")) return;
    setSendingId(leadId);
    try {
      const res = await api(`/api/admin/leads/${leadId}/send-followup`, token, { method: "POST" });
      setToast(`Sent · ${res.resend_id?.slice(0, 8)}…`);
      setTimeout(() => setToast(""), 3500);
      await refresh(token);
    } catch (e) {
      setToast(`Failed: ${e.message}`);
      setTimeout(() => setToast(""), 4500);
    } finally {
      setSendingId(null);
    }
  };

  const updateStatus = async (leadId, status) => {
    // optimistic update
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await api(`/api/admin/leads/${leadId}/status`, token, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      const closing = ["contacted", "booked", "opted_out", "closed"].includes(status);
      setToast(closing ? "Status updated · sequence will stop" : "Status updated");
      setTimeout(() => setToast(""), 3000);
      if (status === "opted_out") await refresh(token);
    } catch (e) {
      setToast(`Failed: ${e.message}`);
      setTimeout(() => setToast(""), 4500);
      await refresh(token);
    }
  };

  const toggleSubscription = async (lead) => {
    const next = !lead.unsubscribed;
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, unsubscribed: next } : l)));
    try {
      await api(`/api/admin/leads/${lead.id}/subscription`, token, {
        method: "POST",
        body: JSON.stringify({ unsubscribed: next }),
      });
      setToast(next ? "Contact unsubscribed" : "Contact resubscribed");
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setToast(`Failed: ${e.message}`);
      setTimeout(() => setToast(""), 4500);
      await refresh(token);
    }
  };

  /* ----------------- LOGIN ----------------- */
  if (!authed) {
    return (
      <main className="min-h-screen bg-[var(--cream)] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="rounded-3xl bg-white p-8 shadow-xl border border-black/5">
            <div className="w-12 h-12 rounded-2xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h1 className="mt-6 font-serif text-3xl text-[var(--navy)]">Leads dashboard</h1>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">Enter the admin token to view leads and send follow-ups.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <input
                type="password"
                value={inputToken}
                onChange={(e) => setInputToken(e.target.value)}
                placeholder="Admin token"
                className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white focus:outline-none focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20"
                data-testid="admin-token-input"
              />
              {error && <div className="text-sm text-[var(--terracotta-deep)]">{error}</div>}
              <button type="submit" className="w-full pill py-3 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium" data-testid="admin-login-btn">
                Unlock
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  /* ----------------- DASHBOARD ----------------- */
  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <h1 className="mt-3 font-serif text-3xl lg:text-4xl text-[var(--navy)]">Leads dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refresh(token)} className="pill px-4 py-2 rounded-full border border-[var(--navy)]/20 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] text-sm inline-flex items-center gap-2" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="pill px-4 py-2 rounded-full text-sm text-[var(--muted)] hover:text-[var(--terracotta)] inline-flex items-center gap-1.5">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Automation setup */}
        <AutomationPanel info={autoInfo} open={showSetup} onToggle={() => setShowSetup((v) => !v)} />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total" n={stats.total || 0} colour="#1B2D4F" />
          <StatCard label="Quiz" n={stats.by_kind?.quiz || 0} colour="#B8472D" />
          <StatCard label="EU Checklist" n={stats.by_kind?.eu_quiz || 0} colour="#3D5A80" />
          <StatCard label="Checklist" n={stats.by_kind?.checklist || 0} colour="#6B7A4F" />
          <StatCard label="Calculator" n={stats.by_kind?.calculator || 0} colour="#C9A24A" />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-medium mr-2 inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Filter</span>
          {["all", "quiz", "eu_quiz", "checklist", "calculator"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === k
                  ? "bg-[var(--terracotta)] text-[var(--cream)] border-[var(--terracotta)]"
                  : "bg-white border-black/10 text-[var(--ink)] hover:border-[var(--terracotta)]/40"
              }`}
              data-testid={`admin-filter-${k}`}
            >
              {k === "all" ? "All" : KIND_META[k]?.label}
            </button>
          ))}
        </div>

        {/* Leads */}
        <div className="space-y-3">
          {leads.length === 0 && !loading && (
            <div className="rounded-2xl p-10 bg-white border border-black/5 text-center text-[var(--muted)]">No leads yet in this view.</div>
          )}
          {leads.map((lead) => {
            const meta = KIND_META[lead.kind] || {};
            const Icon = meta.icon || Mail;
            const tier = TIER_META[lead.tier];
            const expanded = expandedId === lead.id;
            const followups = lead.followups_sent || [];
            return (
              <div key={lead.id} className="rounded-2xl bg-white border border-black/5 overflow-hidden" data-testid={`admin-lead-${lead.id}`}>
                <div className="p-5 flex items-start gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: meta.colour || "#666" }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-[var(--navy)]">{lead.name}</div>
                      {tier && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold" style={{ background: tier.bg, color: tier.fg }}>
                          {tier.label}{lead.score ? ` · ${lead.score}` : ""}
                        </span>
                      )}
                      {followups.length > 0 && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold bg-[var(--olive)]/15 text-[var(--olive)] inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                      )}
                      {lead.enrolled && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold bg-[var(--terracotta)]/12 text-[var(--terracotta)] inline-flex items-center gap-1" data-testid={`admin-enrolled-${lead.id}`} title="Enrolled in Resend automation">
                          <Zap className="w-3 h-3" /> Enrolled
                        </span>
                      )}
                      {lead.unsubscribed && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold bg-black/8 text-[var(--muted)] inline-flex items-center gap-1" data-testid={`admin-unsub-${lead.id}`}>
                          <BellOff className="w-3 h-3" /> Unsubscribed
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--ink-soft)] truncate">{lead.email}</div>
                    <div className="text-xs text-[var(--muted)] mt-1">
                      {lead.kind} · {new Date(lead.created_at).toLocaleString("en-GB")}
                      {lead.estimate?.low && ` · £${lead.estimate.low.toLocaleString()}–£${lead.estimate.high.toLocaleString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <select
                      value={lead.status || "new"}
                      onChange={(e) => updateStatus(lead.id, e.target.value)}
                      className="text-xs font-medium rounded-full px-3 py-2 border border-black/10 bg-white text-[var(--ink)] focus:outline-none focus:border-[var(--terracotta)] cursor-pointer"
                      style={{
                        background: (STATUS_META[lead.status || "new"] || {}).bg,
                        color: (STATUS_META[lead.status || "new"] || {}).fg,
                        borderColor: "transparent",
                      }}
                      data-testid={`admin-status-${lead.id}`}
                      title="Lead status — closing statuses stop the sequence"
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} style={{ background: "#fff", color: "#1A1A1A" }}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => toggleSubscription(lead)}
                      className="pill inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-black/10 text-xs font-medium hover:border-[var(--terracotta)]/40"
                      data-testid={`admin-sub-toggle-${lead.id}`}
                      title={lead.unsubscribed ? "Resubscribe contact" : "Unsubscribe contact"}
                    >
                      {lead.unsubscribed ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                      {lead.unsubscribed ? "Resubscribe" : "Unsubscribe"}
                    </button>
                    <button
                      onClick={() => setExpandedId(expanded ? null : lead.id)}
                      className="text-xs text-[var(--muted)] hover:text-[var(--terracotta)]"
                      data-testid={`admin-lead-expand-${lead.id}`}
                    >
                      {expanded ? "Hide" : "Details"}
                    </button>
                    <button
                      onClick={() => sendFollowup(lead.id)}
                      disabled={sendingId === lead.id}
                      className="pill inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--navy)] text-[var(--cream)] text-sm font-medium disabled:opacity-60"
                      data-testid={`admin-send-${lead.id}`}
                    >
                      {sendingId === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {sendingId === lead.id ? "Sending…" : "Send now"}
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="border-t border-black/5 p-5 bg-[var(--cream-deep)]/40 text-xs font-mono whitespace-pre-wrap break-all text-[var(--ink)]">
                    {JSON.stringify(lead, null, 2)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-[var(--navy)] text-[var(--cream)] text-sm shadow-2xl" data-testid="admin-toast">
          {toast}
        </div>
      )}
    </main>
  );
}

function StatCard({ label, n, colour }) {
  return (
    <div className="rounded-2xl p-5 bg-white border border-black/5">
      <div className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: colour }}>{label}</div>
      <div className="mt-2 font-serif text-4xl text-[var(--navy)]">{n}</div>
    </div>
  );
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--cream)] border border-black/5 px-4 py-2.5">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold">{label}</div>
        <div className="text-sm font-mono text-[var(--ink)] truncate">{value}</div>
      </div>
      <button onClick={copy} className="flex-shrink-0 text-[var(--muted)] hover:text-[var(--terracotta)]" title="Copy" data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {copied ? <Check className="w-4 h-4 text-[var(--olive)]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function AutomationPanel({ info, open, onToggle }) {
  const ok = info?.ok;
  const events = info?.events || { enrolled: "lead_enrolled", closed: "lead_closed" };
  return (
    <div className="mb-6 rounded-2xl bg-white border border-black/5 overflow-hidden" data-testid="automation-panel">
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 p-5 text-left" data-testid="automation-panel-toggle">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <div className="font-medium text-[var(--navy)]">Email automation setup</div>
            <div className="text-xs text-[var(--muted)]">
              {info == null ? "Checking Resend…" : ok ? "Connected · audiences ready" : "Action needed · Full Access key required"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {info != null && (
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${ok ? "bg-[var(--olive)]/15 text-[var(--olive)]" : "bg-[var(--terracotta)]/12 text-[var(--terracotta)]"}`}>
              {ok ? "Ready" : "Setup"}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-[var(--muted)] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-black/5 p-5 space-y-4">
          {!ok && info?.reason === "send_only_key" && (
            <div className="flex items-start gap-3 rounded-xl bg-[var(--terracotta)]/8 border border-[var(--terracotta)]/20 p-4" data-testid="automation-key-warning">
              <AlertTriangle className="w-4 h-4 text-[var(--terracotta)] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[var(--ink)]">
                Your current Resend API key is <b>send-only</b>, so contacts can&apos;t be added to audiences and the drip sequence can&apos;t start automatically. The instant tool-completion email still works.
                <br />Fix: in Resend go to <b>API Keys → Create API Key → Permission: Full access</b>, then send me the new key.
              </div>
            </div>
          )}
          {ok && (
            <div className="grid sm:grid-cols-2 gap-3">
              <CopyField label="Free Tools audience ID" value={info.audiences.free_tools} />
              <CopyField label="Newsletter audience ID" value={info.audiences.newsletter} />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <CopyField label="Trigger event (start)" value={events.enrolled} />
            <CopyField label="Exit event (stop)" value={events.closed} />
          </div>
          <div className="text-xs text-[var(--ink-soft)] leading-relaxed rounded-xl bg-[var(--cream)] p-4">
            <b>How to build the sequence in Resend:</b> Automations → New → Trigger = custom event <span className="font-mono">{events.enrolled}</span> on the <i>Free Tools</i> audience → add your emails + delays → add an <b>Exit on event</b> step listening for <span className="font-mono">{events.closed}</span> so booked/contacted/closed leads stop receiving emails.
          </div>
        </div>
      )}
    </div>
  );
}
